import React, { forwardRef, memo, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import Portal from '@polestar/component-warehouse-react/components/portal/portal';
import Backdrop from '@polestar/component-warehouse-react/components/backdrop/Backdrop';
import { css } from '@emotion/react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import Positions from '@polestar/component-warehouse-react/tokens/positions/Positions';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { calculatePosition, getPlacements, } from '@polestar/component-warehouse-react/components/attached-popover/AttachedPopover.helpers';
import Alignments from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import useResizeCallback, { ResizeCallbackTypes, } from '@polestar/component-warehouse-react/hooks/useResizeCallback';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { useUpdateEffect } from '@polestar/component-warehouse-react/hooks/useUpdateEffect';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { usePopover } from '@polestar/component-warehouse-react/contexts/popover';
const BottomFirst = [
    Positions.bottomRight,
    Positions.bottomLeft,
    Positions.bottomCenter,
    Positions.topRight,
    Positions.topLeft,
    Positions.topCenter,
];
const TopFirst = [
    Positions.topRight,
    Positions.topLeft,
    Positions.topCenter,
    Positions.bottomRight,
    Positions.bottomLeft,
    Positions.bottomCenter,
];
const AttachedPopover = forwardRef(({ id, attachTo, attachIn, children, onClose, offset = {}, minimumDimensions = {}, backdropProps = {}, preferredPlacement = BottomFirst, onPlacementChange, forcePlacement = false, stretch = true, onVisibilityChange, isHiddenWhenScrolled = false, ...props }, ref) => {
    const memoizedId = useSSRSafeId('attached-popover', id);
    const [positionInnerNode, setPositionInnerNode] = useState(null);
    const [position, setPosition] = useState(null);
    const { top, left, width, height, openSide, mainAxisAlignment, crossAxisAlignment } = position || {};
    const { isRTL } = useDirection();
    const previousPlacement = useRef();
    const placementOffset = useRef(undefined);
    if (preferredPlacement === Alignments.top) {
        preferredPlacement = TopFirst;
    }
    else if (preferredPlacement === Alignments.bottom) {
        preferredPlacement = BottomFirst;
    }
    const handleClose = useCallback(() => {
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose]);
    const calculateAttachTo = useCallback(() => {
        if (attachTo && positionInnerNode) {
            /**
             * get base constants
             */
            const { top: attachedTop, left: attachedLeft, height: attachedHeight, width: attachedWidth, } = attachTo.getBoundingClientRect();
            const { height: popoverHeight, width: popoverWidth } = positionInnerNode === null || positionInnerNode === void 0 ? void 0 : positionInnerNode.getBoundingClientRect();
            // Skip
            if (popoverHeight === 0 || popoverWidth === 0) {
                return;
            }
            const body = document.body;
            const docEl = document.documentElement;
            const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
            const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
            const clientTop = docEl.clientTop || body.clientTop || 0;
            const clientHeight = docEl.clientHeight || body.clientHeight || 0;
            const clientLeft = docEl.clientLeft || body.clientLeft || 0;
            const clientWidth = docEl.clientWidth || body.clientWidth || 0;
            const baseLeft = attachedLeft + scrollLeft - clientLeft;
            const baseTop = attachedTop + scrollTop - clientTop;
            const preferredPlacementArray = !Array.isArray(preferredPlacement)
                ? [preferredPlacement]
                : preferredPlacement;
            const placements = getPlacements({
                preferredPlacementArray,
                attachedLeft,
                attachedTop,
                attachedWidth,
                attachedHeight,
                clientWidth,
                clientHeight,
                popoverHeight,
                popoverWidth,
                isRTL,
            });
            // @ts-ignore
            const firstThatFits = Object.keys(placements).find((key) => placements[key].fits);
            const placement = !firstThatFits && !forcePlacement
                ? // If no position is found, but the popover fits above the IconButton
                    attachedTop - popoverHeight >= 0
                        ? Positions.topMiddle
                        : // If no position is found, but the popover fits below the IconButton
                            attachedTop + popoverHeight <= clientHeight || attachedTop + attachedHeight > 0
                                ? Positions.bottomMiddle
                                : // This should never happen, but if it does, use old fallback
                                    previousPlacement.current || preferredPlacementArray[0]
                : // This is used when forcePlacement === true
                    // ensure that if nothing fits to keep the previous result or use the preferred placement list to prevent getting into a loop of opposite placements
                    !firstThatFits || forcePlacement
                        ? previousPlacement.current || preferredPlacementArray[0]
                        : firstThatFits;
            /**
             * calculate position
             */
            const { top, left, width, height, mainAxisAlignment, crossAxisAlignment } = calculatePosition({
                placement,
                baseTop,
                baseLeft,
                attachedHeight,
                attachedWidth,
                clientWidth,
                isRTL,
            });
            // keep value for next calculation
            previousPlacement.current = placement;
            // Calculate arrow offset for when topMiddle or bottomMiddle is needed
            const { x: innerPopoverX } = positionInnerNode.getBoundingClientRect();
            const intendedPlacement = attachedLeft + attachedWidth / 2;
            placementOffset.current = {
                x: intendedPlacement - innerPopoverX,
            };
            setPosition({
                top,
                left,
                width,
                height,
                openSide: placement.split(' ')[0],
                mainAxisAlignment,
                crossAxisAlignment,
                placement,
            });
        }
    }, [forcePlacement, positionInnerNode, preferredPlacement, attachTo, isRTL]);
    const positionInnerRef = useResizeCallback((node) => {
        setPositionInnerNode(node);
    }, [], ResizeCallbackTypes.both);
    // Reposition on resize
    useEffect(() => {
        let timeout;
        const throttledCalculate = () => {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                calculateAttachTo();
            }, 100);
        };
        const throttledScrollCalculate = () => {
            if (isHiddenWhenScrolled) {
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }
            else {
                throttledCalculate();
            }
        };
        window.addEventListener('resize', throttledCalculate);
        window.addEventListener('scroll', throttledScrollCalculate);
        return () => {
            clearTimeout(timeout);
            window.removeEventListener('resize', throttledCalculate);
            window.removeEventListener('scroll', throttledScrollCalculate);
        };
    }, [calculateAttachTo, onClose, isHiddenWhenScrolled]);
    useEffect(() => {
        document.addEventListener('visibilitychange', handleClose);
        return () => {
            document.removeEventListener('visibilitychange', handleClose);
        };
    }, [handleClose]);
    // When the popup switches position, the padding set by the offset is still on the "old side".
    // This forces it to do another loop and recalculate with the new spacing
    const forceRecheck = offset.top || offset.left || offset.bottom || offset.right ? openSide : false;
    useEffect(() => {
        calculateAttachTo();
    }, [calculateAttachTo, forceRecheck]);
    const placement = position === null || position === void 0 ? void 0 : position.placement;
    useEffect(() => {
        placement && (onPlacementChange === null || onPlacementChange === void 0 ? void 0 : onPlacementChange(placement, placementOffset.current));
    }, [placement, onPlacementChange]);
    /**
     * If we do this in the render, it will cause a flash of position
     * This is because the attached popover is possible called together with a "focus initial", which fires
     * a scroll after a requestAnimationFrame. We put the "visible=true" below after that requestAnimationFrame.
     */
    const visible = !!position && !!placement;
    useUpdateEffect(() => {
        onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange(visible);
    }, [visible, onVisibilityChange]);
    useEffect(() => {
        return () => {
            onVisibilityChange === null || onVisibilityChange === void 0 ? void 0 : onVisibilityChange(false);
        };
    }, [onVisibilityChange]);
    // register popover
    usePopover(memoizedId);
    return (React.createElement(Portal, { insideContainer: attachIn },
        React.createElement(Backdrop, { onClose: handleClose, visible: true, allowScroll: true, withBackground: false, ...backdropProps }),
        React.createElement(PositionWrapper, { ref: ref, visible: visible, top: top, left: left, width: width, height: height, mainAxisAlignment: mainAxisAlignment, crossAxisAlignment: crossAxisAlignment, ...props },
            React.createElement(PositionInner, { "data-type": "attached-popover-content", ref: positionInnerRef, offset: offset, openSide: openSide, minimumDimensions: minimumDimensions, preferredPlacement: preferredPlacement, placement: placement, stretch: stretch, id: memoizedId }, children))));
});
AttachedPopover.displayName = 'AttachedComponent';
export default memo(AttachedPopover);
const Container = styled.div ``;
Container.displayName = `Container`;
const PositionWrapper = styled('div', shouldNotForwardProps(true, 'width', 'height', 'top', 'left', 'minimumDimensions'))(({ visible, width = 0, height = 0, top = 0, left = 0, mainAxisAlignment, crossAxisAlignment }) => css(css `
      position: absolute;
      inset-block-start: ${top}px;
      inset-inline-start: ${left}px;
      width: ${width}px;
      height: ${height}px;
      z-index: ${LayoutIndexes.popover};
      opacity: ${visible ? 1 : 0};
      display: flex;
      justify-content: ${mainAxisAlignment};
      align-items: ${crossAxisAlignment};
      transition: opacity 0.05s ease-in-out 0.00001s;
    `, 
// ensure that in RTL the calculations will not create a scrollbar at the bottom
// while the element is there but opacity:0 which create the scrollbar
// Example: WithTooltip.positioning story & issue with top-left Tooltip trigger
!visible &&
    css `
        overflow: hidden;
      `));
const PositionInner = styled('div', shouldNotForwardProps(true, 'offset', 'stretch'))(({ offset, openSide, minimumDimensions, stretch, preferredPlacement, placement }) => {
    const isTopOrBottomLeft = preferredPlacement === Positions.topLeft || preferredPlacement === Positions.bottomLeft;
    const isTopOrBottomRight = preferredPlacement === Positions.topRight || preferredPlacement === Positions.bottomRight;
    return css(css `
      min-height: 100%;
      position: absolute;
      display: flex;
      justify-content: ${(placement === null || placement === void 0 ? void 0 : placement.indexOf('left')) > -1
        ? 'flex-end'
        : (placement === null || placement === void 0 ? void 0 : placement.indexOf('right')) > -1
            ? 'flex-start'
            : 'center'};

      ${openSide === 'top' && (offset === null || offset === void 0 ? void 0 : offset.top) && `padding-block-end: ${offset === null || offset === void 0 ? void 0 : offset.top};`};
      ${openSide === 'bottom' && (offset === null || offset === void 0 ? void 0 : offset.bottom) && `padding-block-start: ${offset === null || offset === void 0 ? void 0 : offset.bottom};`};
      ${openSide === 'left' && (offset === null || offset === void 0 ? void 0 : offset.left) && `padding-inline-end: ${offset === null || offset === void 0 ? void 0 : offset.left};`};
      ${openSide === 'right' && (offset === null || offset === void 0 ? void 0 : offset.right) && `padding-inline-start: ${offset === null || offset === void 0 ? void 0 : offset.right};`}

      ${openSide === 'left' && `justify-content: flex-end;`};
      ${openSide === 'right' && `justify-content: flex-start;`};

      ${isTopOrBottomLeft && `justify-content: flex-end;`};
      ${isTopOrBottomRight && `justify-content: flex-start;`};
    `, stretch &&
        css `
        min-width: 100%;
      `, (minimumDimensions === null || minimumDimensions === void 0 ? void 0 : minimumDimensions.width) &&
        css `
        width: ${typeof minimumDimensions.width === 'number'
            ? `${minimumDimensions.width}px`
            : minimumDimensions.width};
      `, (minimumDimensions === null || minimumDimensions === void 0 ? void 0 : minimumDimensions.height) &&
        css `
        height: ${typeof minimumDimensions.height === 'number'
            ? `${minimumDimensions.height}px`
            : minimumDimensions.height};
      `);
});
